import DOMPurify from 'dompurify';
import React from 'react';

import {isError} from 'graphql-client/Error';

import useResizeSVG from '../../ui-library/hooks/useResizeSVG';
import {MonomerQuery} from './__generated/MonomerQuery';

type MonomerChemicalStructureImage = MonomerQuery['monomers'][number]['chemicalStructureImage'];

/**
 * A React element visualizing the chemical structure of the given monomer.
 * Optionally adds CSS classes to the containing <div> and <svg> image, useful for styling with Aphrodite
 * If the chemicalStructureImage prop is an error node, this element displays an error message instead.
 *
 * @param chemicalStructureImage SVGNode if chemical structure is valid otherwise ErrorNode
 * @param className CSS class to give to the containing <div>
 * @param svgClassName CSS class to give to the chemical structure <svg>
 * @returns a React element with a chemical structure image or a div with an error message
 */
function MonomerChemicalStructure({
  chemicalStructureImage,
  className,
  svgClassName,
}: {
  chemicalStructureImage: MonomerChemicalStructureImage;
  className?: string;
  svgClassName?: string;
}): JSX.Element {
  const base64Svg = !isError(chemicalStructureImage) ? chemicalStructureImage.base64Svg : null;
  const errorMessage = isError(chemicalStructureImage) ? chemicalStructureImage.message : null;

  // The chemistry service always returns square SVGs with lots of unnecessary vertical padding, causing most
  // chemicals to show up smaller. There's no way to modify the SVG's viewBox if it's included as an <img>,
  // but it is possible if the SVG is included as raw HTML (dangerouslySetInnerHTML).
  const svgContainerRef = useResizeSVG<HTMLDivElement>(svgClassName);

  if (base64Svg) {
    const htmlSvg = DOMPurify.sanitize(window.atob(base64Svg));

    return (
      // eslint-disable-next-line react/no-danger
      <div ref={svgContainerRef} className={className} dangerouslySetInnerHTML={{__html: htmlSvg}} />
    );
  } else {
    return <div>{errorMessage}</div>;
  }
}

export default React.memo(MonomerChemicalStructure);
